import { useEffect, useState } from "react";
import { VisualizationControlStates, VisualizationMode } from "./types";
import {
    DEFAULT_STACKED_CONTROLS,
    DEFAULT_LEGEND_CONTROLS,
    DEFAULT_NAVIGATOR_CONTROLS,
} from "./consts";

// // // //

/**
 * Custom React hook that manages and initializes the state of visualization controls based on the current visualization mode.
 * It dynamically updates the active states of stacked, legend, and navigator controls using the initial states defined in the
 * visualization mode or defaults if not specified. This hook also provides toggle functions for each control, allowing
 * user interaction to show or hide these elements in the visualization.
 *
 * @param {VisualizationMode} mode - The current visualization mode that determines the initial state of the controls.
 * @returns {VisualizationControlStates} The current state of the visualization controls, including active states and toggle functions.
 */
export function useVisualizationControlState(
    mode: VisualizationMode,
): VisualizationControlStates {
    // Initialize control states based on the current visualization mode or use default values.
    const vc = mode.visualizationControls;
    const ctrls = {
        stacked: (vc && vc.stacked) ?? DEFAULT_STACKED_CONTROLS,
        legend: (vc && vc.legend) ?? DEFAULT_LEGEND_CONTROLS,
        navigator: (vc && vc.navigator) ?? DEFAULT_NAVIGATOR_CONTROLS,
    };

    // State hooks to manage the active states of the controls
    const [stackedActive, setShowStacked] = useState(ctrls.stacked.active);
    const [legendActive, setShowLegend] = useState(ctrls.legend.active);
    const [navigatorActive, setShowNavigator] = useState(
        ctrls.navigator.active,
    );

    // useEffect hooks to update control states when the visualization mode changes
    useEffect(() => {
        if (!ctrls.stacked.forceUpdate) return;
        setShowStacked(ctrls.stacked.active);
    }, [mode]);

    useEffect(() => {
        if (!ctrls.legend.forceUpdate) return;
        setShowLegend(ctrls.legend.active);
    }, [mode]);

    useEffect(() => {
        if (!ctrls.navigator.forceUpdate) return;
        setShowNavigator(ctrls.navigator.active);
    }, [mode]);

    // Toggle functions to change the active state of each control.
    const toggleStacked = () => setShowStacked(!stackedActive);
    const toggleLegend = () => setShowLegend(!legendActive);
    const toggleNavigator = () => setShowNavigator(!navigatorActive);

    return {
        stacked: {
            ...ctrls.stacked,
            active: stackedActive,
            toggle: toggleStacked,
        },
        legend: {
            ...ctrls.legend,
            active: legendActive,
            toggle: toggleLegend,
        },
        navigator: {
            ...ctrls.navigator,
            active: navigatorActive,
            toggle: toggleNavigator,
        },
    };
}
